import Column from './Column.js';
import ColumnStore from '../data/ColumnStore.js';
import '../../Core/widget/ColorField.js';

/**
 * @module Grid/column/ColorColumn
 */

/**
 * A column that displays color values (built-in color classes or CSS colors) as a colored element similar to
 * the {@link Core.widget.ColorField}. When the user clicks the element, a {@link Core.widget.ColorPicker} lets the user
 * select from a range of colors.
 *
 * {@inlineexample Grid/column/ColorColumn.js}
 *
 * ```javascript
 * new Grid({
 *    columns : [
 *       {
 *          type   : 'color',
 *          field  : 'color',
 *          text   : 'Color'
 *       }
 *    ]
 * });
 * ```
 *
 * @extends Grid/column/Column
 * @classtype color
 */
export default class ColorColumn extends Column {
    static $name = 'ColorColumn';

    static type = 'color';

    static fields = [
        /**
         * The `colors` property can be an array of string CSS colors or of objects with `color`
         * and `text` properties from which the user can chose from. This will override the
         * {@link Core.widget.ColorPicker#config-colors pickers default colors}.
         *
         * Provide an array of string CSS colors:
         * ```javascript
         * new Grid({
         *    columns : [
         *       {
         *          type   : 'color',
         *          field  : 'color',
         *          text   : 'Color',
         *          colors : ['#00FFFF', '#F0FFFF', '#89CFF0', '#0000FF', '#7393B3']
         *       }
         *    ]
         * });
         * ```
         * @prp {Array<String|ColorDescriptor>} colors
         */
        'colors',

        /**
         * Adds an option in the picker to set no background color
         * @prp {Boolean} addNoColorItem
         * @default true
         */
        { name : 'addNoColorItem', defaultValue : true }
    ];

    /**
     * @hideconfigs editor, cellEditor
     */

    static defaults = {
        align  : 'center',
        editor : 'colorfield'
    };

    preventCellEditViaMouse = true;

    construct() {
        super.construct(...arguments);

        const
            me                 = this,
            { editor, colors } = me;

        if (colors?.length) {
            editor.colors = colors;
        }
        editor.addNoColorItem = me.addNoColorItem;

        editor.ion({ colorSelected : 'onColorSelected', thisObj : me });
    }

    applyValue(useProp, field, value) {
        if (!this.isConstructing) {
            const { editor } = this;

            if (field === 'colors') {
                editor.colors = value;
            }
            else if (field === 'addNoColorItem') {
                editor.addNoColorItem = value;
            }
        }

        super.applyValue(...arguments);
    }

    get picker() {
        return this.editor.picker;
    }

    defaultRenderer({ value }) {
        let colorClass      = 'b-empty',
            backgroundColor = value;

        if (value) {
            const colorClassName = this.picker.getColorClassName(value);

            if (colorClassName) {
                colorClass      = colorClassName;
                backgroundColor = null;
            }
            else {
                colorClass = '';
            }
        }

        return {
            className : 'b-color-cell-inner ' + colorClass,
            style     : {
                backgroundColor
            },
            'data-btip' : value
        };
    }

    // Handles single click edit on the color box
    // ColorColumn didn't have an editor previously, but now do, so this handles non-celledit edit
    onCellClick({ grid, record, target }) {
        if (target.classList.contains('b-color-cell-inner') && !this.readOnly &&
            !grid.readOnly && !record.isSpecialRow && !record.readOnly &&
            !grid.features.cellEdit?.finishEditingPromise
        ) {
            const
                { picker } = this,
                value      = record.get(this.field);

            this._editingRecord = record;

            picker.deselectAll();
            picker.select(value);
            picker.refresh();
            picker.showBy(target);
        }
    }

    onColorSelected({ color }) {
        if (!this.grid.features.cellEdit?.isEditing && this._editingRecord) {
            this._editingRecord.set(this.field, color);
        }
    }
}

ColumnStore.registerColumnType(ColorColumn);
